import { ReactNode, useCallback, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { SxProps } from '@mui/material';
import { poiState } from '@/components/Analysis/modules/pointOfInterest';
import { Navigation } from '@/components/shared/Navigation/Navigation';
import { NavigationItem } from '@/types';
import { abyssColors } from '@/theme/colors';
import { generatePoiDisplayName } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { PointOfInterest } from '@/__generated__/graphql';
import { useInsightsPageParams } from '@/hooks';

type Props = {
  sx?: SxProps;
  children?: ReactNode;
  location?: string;
};

export const PointOfInterestNavigation = ({ sx, children, location = '' }: Props) => {
  const structurePois = useAtomValue(poiState.structurePois);
  const [selectedPoiId, setSelectedPoiId] = useAtom(poiState.selectedPointOfInterestId);
  const setSelectedPoi = useSetAtom(poiState.selectedPointOfInterest);
  const selectedPoi = useAtomValue(poiState.selectedPointOfInterest) as PointOfInterest | undefined;
  const { setPoi } = useInsightsPageParams();

  const poisData = useMemo(
    () =>
      structurePois?.map((poi) => ({
        id: poi.id,
        name: generatePoiDisplayName({ name: poi.name, poiId: poi.poiId, poiType: poi.type }),
      })) ?? [],
    [structurePois]
  );

  const handleChange = useCallback(
    (poiData: Partial<NavigationItem>) => {
      if (!poiData.id) return;
      setSelectedPoi(undefined);
      setSelectedPoiId(poiData.id);
      setPoi(poiData.id);
    },
    [setSelectedPoi, setSelectedPoiId, setPoi]
  );

  const displayName = selectedPoi
    ? generatePoiDisplayName({
        name: selectedPoi.name,
        poiId: selectedPoi.poiId,
        poiType: selectedPoi.type,
      })
    : '';

  return (
    <Stack sx={sx}>
      <Navigation
        handleChange={handleChange}
        selectedItemId={selectedPoiId}
        items={poisData}
        toolTip={displayName}
        location={location}
      >
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 200,
            color: abyssColors.primary[700],
            fontWeight: '700',
          }}
        >
          {displayName}
        </Typography>
      </Navigation>
      {children}
    </Stack>
  );
};
